import { createContext, useContext, useState } from 'react'

const VALID = ['en', 'es', 'pt']

// Normaliza el valor recibido (soporta 'ES', 'es', 'EN', 'PT', etc.)
const normalize = (value) => {
    if (!value) return null
    const v = value.toString().toLowerCase()
    if (VALID.includes(v)) return v
    if (v.startsWith('es')) return 'es'
    if (v.startsWith('pt')) return 'pt'
    if (v.startsWith('en')) return 'en'
    return null
}

const detectLang = () => {
    // SSR / entornos sin window: fallback seguro
    if (typeof window === 'undefined') return 'en'

    try {
        const params = new URLSearchParams(window.location.search)

        // Soporta ?Language=ES, ?language=es, ?lang=pt (case-insensitive)
        const queryValue =
            params.get('Language') || params.get('language') || params.get('lang')
        const query = normalize(queryValue)
        if (query) {
            sessionStorage.setItem('lang', query)
            return query
        }

        const stored = sessionStorage.getItem('lang')
        if (stored && VALID.includes(stored)) return stored

        const b = (navigator.language || navigator.userLanguage || 'en').toLowerCase()
        if (b.startsWith('es')) return 'es'
        if (b.startsWith('pt')) return 'pt'
    } catch (e) {
        // sessionStorage / window pueden fallar en algunos contextos, no rompas la app
    }

    return 'en'
}

// Default context value - NUNCA null. Asi si un componente usa los hooks
// fuera del <LanguageProvider>, no revienta con "Cannot read properties of null".
const defaultContextValue = {
    lang: 'en',
    changeLang: () => { },
}

const LanguageContext = createContext(defaultContextValue)

export function LanguageProvider({ children }) {
    const [lang, setLang] = useState(() => detectLang())

    const changeLang = (code) => {
        const normalized = normalize(code)
        if (!normalized) return
        try {
            sessionStorage.setItem('lang', normalized)
        } catch (e) { }
        setLang(normalized)
    }

    return (
        <LanguageContext.Provider value={{ lang, changeLang }}>
            {children}
        </LanguageContext.Provider>
    )
}

// Hooks defensivos: siempre devuelven algo utilizable
export const useLanguage = () => {
    const ctx = useContext(LanguageContext)
    return ctx?.lang ?? 'en'
}

export const useChangeLang = () => {
    const ctx = useContext(LanguageContext)
    return ctx?.changeLang ?? (() => { })
}

/**
 * Helper para usar en cada componente.
 * Las traducciones viven en /src/translations/{NombreComponente}.js
 *
 * Uso:
 *   import translations from '../../translations/MiComponente'
 *   import { useTranslations } from '../context/LanguageContext'
 *
 *   const t = useTranslations(translations)
 *   return <h1>{t.title}</h1>
 *
 * Si el idioma actual no existe en el archivo o falta una key, cae a ingles.
 * Si el diccionario es null/undefined, devuelve {} (no revienta).
 */
export const useTranslations = (dictionary) => {
    const lang = useLanguage()
    if (!dictionary) return {}
    const current = dictionary[lang] || {}
    const fallback = dictionary.en || {}
    return { ...fallback, ...current }
}
